import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { getProject, saveProjectToWeb, createProject, subscribeProject } from '../firebase/workspaceService';
import { getEditAccess } from '../firebase/shareCodeService';
import { useAuth } from '../context/AuthContext';
import { loadDesign, gridSizeCodeToCells, selectRoom } from '../store/houseSlice';
import { useT, LANGS } from '../i18n';
import ThreeDRoom from '../components/RoomEditor/ThreeDRoom';
import RoomEditor from '../components/RoomEditor/RoomEditor';
import House3DView from '../components/House3DView/House3DView';
import { SocialBar } from '../components/Social';
import OwnerBadge from '../components/OwnerBadge';
import { SOCIAL_KINDS } from '../firebase/socialService';
import './SharedProject.css';

// Public, unauthenticated, READ-ONLY view of a saved design.
//   /share/:userId/:projectId
// The link is generated by the owner (Share button → sets the project's
// `shared` flag, see workspaceService.setProjectShared). Anyone with the link
// can look at the design in 3D — both the full house and each room — but there
// is no way to edit: ThreeDRoom is mounted with readOnly (no Edit button, so
// edit mode can never be entered) and House3DView with shared (no save / back).
//
// Requires the Firestore rules to allow reading a project whose shared==true —
// see firestore-share.rules. Without that rule change, getProject() below is
// rejected for logged-out viewers and the page shows "not available".

export default function SharedProject() {
  const { userId, projectId } = useParams();
  // ── EMBEDDED IN THE LIGHTBOX ───────────────────────────────────────────────
  // `?embed=1` drops this page's own top bar and leaves the viewer alone, so
  // the gallery lightbox can show a real 3D walkthrough where a photo would be
  // — see GalleryLightbox.
  //
  // AN IFRAME, not this component rendered inline, and that is deliberate: the
  // 3D view reads its project from the SAME Redux store the editor uses.
  // Mounting it inside the feed would mean loading someone else's project into
  // that store, next to whatever the viewer already had open. A frame gets its
  // own store and cannot touch this one.
  const embedded = new URLSearchParams(window.location.search).get('embed') === '1';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang, setLang } = useT();

  const rooms   = useSelector((s) => s.house.rooms);
  const designs = useSelector((s) => s.house.designs);
  const selectedRoomId = useSelector((s) => s.house.selectedRoomId);

  const [ready, setReady]       = useState(false);   // design loaded into Redux
  const [view, setView]         = useState('house'); // 'house' | 'room'
  const [roomIdx, setRoomIdx]   = useState(0);
  // Edit access for an editable share: 'na' (not editable → read-only),
  // 'checking', 'edit' (authorised), or 'denied' (editable but no access).
  const [editState, setEditState] = useState('na');
  // Save-a-copy modal (logged-in viewers duplicate this design into their own
  // workspace).
  const [copyOpen, setCopyOpen]   = useState(false);
  const [copyName, setCopyName]   = useState('');
  const [copyBusy, setCopyBusy]   = useState(false);
  const loadedRef = useRef(false);
  const lastDataRef = useRef(null);   // last applied raw scene string (live-sync guard)
  const editStateRef = useRef('na');  // fresh editState for the subscribe callback
  editStateRef.current = editState;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['sharedProject', projectId],
    queryFn:  () => getProject(projectId),
    enabled:  !!projectId,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // The link carries the owner's uid; make sure it matches the stored project
  // so a link can't be pointed at someone else's project by swapping the uid.
  const ownerMismatch = !!data && !!userId && data.userId !== userId;

  useEffect(() => {
    if (!data || loadedRef.current || ownerMismatch) return;
    loadedRef.current = true;
    const raw    = data?.data;
    lastDataRef.current = raw;
    const parsed = raw && raw !== '{}' && typeof raw === 'string' ? JSON.parse(raw) : null;
    const design = parsed?.version === 1 ? parsed : null;
    if (design) {
      dispatch(loadDesign({ ...design, gridSize: gridSizeCodeToCells(data?.size) }));
    }
    setReady(true);
  }, [data, ownerMismatch, dispatch]);

  // Live sync: reload the design whenever the project doc changes in Firestore,
  // so an editor's saved changes show up for everyone viewing the link — but
  // never overwrite the CURRENT viewer's own in-progress edits.
  useEffect(() => {
    if (!projectId || ownerMismatch) return;
    const unsub = subscribeProject(projectId, (proj) => {
      if (!proj || (userId && proj.userId !== userId)) return;
      if (editStateRef.current === 'edit') return; // don't clobber the active editor
      const raw = proj.data;
      if (!raw || raw === lastDataRef.current) return; // unchanged since last apply
      const parsed = raw && raw !== '{}' && typeof raw === 'string' ? JSON.parse(raw) : null;
      const design = parsed?.version === 1 ? parsed : null;
      if (design) {
        lastDataRef.current = raw;
        dispatch(loadDesign({ ...design, gridSize: gridSizeCodeToCells(proj.size) }));
      }
    });
    return () => unsub();
  }, [projectId, ownerMismatch, userId, dispatch]);

  // Resolve edit access. A non-editable project stays read-only. An editable
  // one grants edit mode to the owner, or to a viewer whose uid has a redeemed,
  // non-expired share code for this project; an expired code → 'expired' (needs
  // a fresh one); anyone else → 'denied'. A timer flips 'edit' → 'expired' if
  // the access lapses mid-session.
  useEffect(() => {
    if (!data || ownerMismatch) return;
    if (!data.shareEditable) { setEditState('na'); return; }
    if (user?.uid && data.userId === user.uid) { setEditState('edit'); return; }
    if (!user?.uid) { setEditState('denied'); return; }
    setEditState('checking');
    let alive = true;
    let timer = null;
    getEditAccess(user.uid, projectId)
      .then(({ granted, expired, expireAt }) => {
        if (!alive) return;
        if (granted) {
          setEditState('edit');
          if (expireAt) {
            const ms = expireAt - Date.now();
            if (ms > 0) timer = setTimeout(() => { if (alive) setEditState('expired'); }, Math.min(ms, 2147483647));
          }
        } else {
          setEditState(expired ? 'expired' : 'denied');
        }
      })
      .catch(() => { if (alive) setEditState('denied'); });
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [data, ownerMismatch, user?.uid, projectId]);

  // Logged-in viewer saves a copy of this design into their OWN workspace, then
  // opens it. Uses the shared project's raw scene data (data.data) verbatim.
  function openCopyModal() {
    if (!canCopy) return;
    setCopyName(data?.projectName ? `${data.projectName} copy` : 'My design');
    setCopyOpen(true);
  }
  async function handleSaveCopy() {
    const name = copyName.trim();
    if (!name || !user?.uid || copyBusy) return;
    setCopyBusy(true);
    try {
      const newId = await createProject(user.uid, name, null, data?.size || 'S');
      await saveProjectToWeb(newId, data?.data || '{}');
      navigate(`/app/${newId}`);
    } catch (err) {
      console.error('[Share] save copy failed:', err);
      setCopyBusy(false);
    }
  }

  // Keep the selected room index valid if the design changes / has fewer rooms.
  useEffect(() => {
    if (roomIdx > rooms.length - 1) setRoomIdx(0);
  }, [rooms.length, roomIdx]);

  // In shared EDIT mode the full RoomEditor (materials + furniture) is rendered
  // for the active room, and it reads the room from redux — keep selectedRoomId
  // in sync with the room picker.
  useEffect(() => {
    if (editState !== 'edit' || view !== 'room' || rooms.length === 0) return;
    const r = rooms[Math.min(roomIdx, rooms.length - 1)];
    if (r) dispatch(selectRoom(r.id));
  }, [editState, view, roomIdx, rooms, dispatch]);

  // getProject resolves to null when the doc doesn't exist (a wrong/deleted id
  // that the rules still let us attempt); a rules denial rejects → isError.
  const notFound     = !isLoading && !isError && !data;
  const notAvailable = isError || ownerMismatch || notFound;

  // ── Loading / error ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="shp-page">
        <TopBar t={t} lang={lang} setLang={setLang} minimal />
        <div className="shp-center">
          <span className="shp-spinner" />
          <span className="shp-center-text">{t('shared_loading')}</span>
        </div>
      </div>
    );
  }

  if (notAvailable) {
    return (
      <div className="shp-page">
        <TopBar t={t} lang={lang} setLang={setLang} minimal />
        <div className="shp-center">
          <span className="shp-center-icon">🔒</span>
          <span className="shp-center-text">{t('shared_not_available')}</span>
          <Link to="/" className="shp-cta shp-cta--solid">{t('shared_back_home')}</Link>
        </div>
      </div>
    );
  }

  // Editable project, but this viewer has no edit access → something is wrong.
  if (editState === 'denied') {
    return (
      <div className="shp-page">
        <TopBar t={t} lang={lang} setLang={setLang} minimal />
        <div className="shp-center">
          <span className="shp-center-icon">🚫</span>
          <span className="shp-center-text">{t('shared_no_edit_access')}</span>
          <Link to="/" className="shp-cta shp-cta--solid">{t('shared_back_home')}</Link>
        </div>
      </div>
    );
  }

  // Edit access has expired → the editor needs a fresh code.
  if (editState === 'expired') {
    return (
      <div className="shp-page">
        <TopBar t={t} lang={lang} setLang={setLang} minimal hideCta />
        <div className="shp-center">
          <img src="/images/Tunify Original logo tab.png" alt="Tunify" className="shp-center-logo" />
          <span className="shp-center-text">{t('shared_edit_expired')}</span>
          <Link to="/" className="shp-cta shp-cta--solid">{t('shared_back_home')}</Link>
        </div>
      </div>
    );
  }

  // Valid, matching project — wait one tick for loadDesign to populate Redux.
  if (!ready) {
    return (
      <div className="shp-page">
        <TopBar t={t} lang={lang} setLang={setLang} minimal />
        <div className="shp-center">
          <span className="shp-spinner" />
          <span className="shp-center-text">{t('shared_loading')}</span>
        </div>
      </div>
    );
  }

  const hasRooms   = rooms.length > 0;
  // A project opened via a PUBLISHED design is someone's portfolio piece, not a
  // template to fork. `sharedByPublish` marks exactly that case (see
  // shareProjectForPublish), so a manually shared link keeps its copy button —
  // sharing by hand is usually meant for collaboration.
  //
  // The owner can still copy their own work.
  const isPublished = data?.sharedByPublish === true;
  const isOwner = !!user?.uid && user.uid === data?.userId;
  // ── WHAT THE AUTHOR GRANTED ────────────────────────────────────────────────
  // A published design is a portfolio piece, so both permissions are off unless
  // the author ticked them when publishing (PublishDesignModal). The owner
  // always has both on their own work.
  //
  // A privately shared project — a link the owner sent someone — keeps the old
  // behaviour: copying was already allowed there, and this must not quietly
  // take it away.
  const allowCopy = data?.allowCopy === true;
  const allowDownload = data?.allowDownload === true;
  const canCopy = isOwner || (isPublished ? allowCopy : true);
  const canDownload = isOwner || !isPublished || allowDownload;

  const activeRoom = hasRooms ? rooms[Math.min(roomIdx, rooms.length - 1)] : null;

  return (
    <div className={`shp-page${embedded ? ' shp-page--embed' : ''}${canDownload ? '' : ' shp-page--nodl'}`}>
      {/* KEPT WHEN EMBEDDED, because this bar is where the room switcher lives
          — hiding it outright left a viewer that could not leave the whole-house
          shot. The CSS strips it down to the House/Room toggle and the room
          list (see .shp-page--embed); the branding, the project name and the
          call to action belong to the standalone page. */}
      <TopBar
        t={t} lang={lang} setLang={setLang}
        projectName={data?.projectName}
        view={view} setView={setView}
        rooms={rooms} roomIdx={roomIdx} setRoomIdx={setRoomIdx}
        canEdit={editState === 'edit'}
        loggedIn={!!user?.uid}
        onSaveCopy={openCopyModal}
        canCopy={canCopy}
        // Hidden in a post ONLY when copying is not granted. It was hidden
        // outright at first — before the publish permissions existed — which
        // meant an author who ticked "allow copy" got a button nobody could
        // see. The permission decides; the embed no longer overrules it.
        hideCta={embedded && !canCopy}
      />

      <div className="shp-view">
        {view === 'house' ? (
          <House3DView shared />
        ) : hasRooms && activeRoom ? (
          editState === 'edit' && rooms.some(r => r.id === selectedRoomId) ? (
            // Authorised editors get the FULL room editor — walls, floors,
            // ceilings and furniture — saving straight to the shared project.
            // Only mounted once a valid room is selected in redux (RoomEditor
            // early-returns on a null room, which would trip rules-of-hooks).
            <RoomEditor sharedMode />
          ) : (
            // key forces a clean remount (camera reset) when switching rooms.
            <ThreeDRoom
              key={activeRoom.id}
              room={activeRoom}
              design={designs[activeRoom.id] || {}}
              readOnly
            />
          )
        ) : (
          <div className="shp-center">
            <span className="shp-center-text">{t('shared_no_rooms')}</span>
          </div>
        )}

        {/* ── SOCIAL ──────────────────────────────────────────────────────
            Overlaid on the 3D view rather than placed in the flow: .shp-view
            is a fixed-height flex child filling the page, and adding a sibling
            row would shrink the canvas on every screen. Mounting the bar also
            registers the view.

            Live listeners belong to an OPEN item only — see socialService
            for why the feed deliberately has none. */}
        <div className="shp-social">
          {/* Whose design this is. A shared link can reach anyone, so the
              author has to be named on the page itself. */}
          {data?.userId && <OwnerBadge uid={data.userId} />}
          <SocialBar kind={SOCIAL_KINDS.PROJECT} id={projectId} ownerUid={data?.userId} />
        </div>

      </div>

      {/* Save-a-copy modal */}
      {copyOpen && (
        <div className="shp-modal-overlay" onClick={() => { if (!copyBusy) setCopyOpen(false); }}>
          <div className="shp-modal" onClick={e => e.stopPropagation()}>
            <h3 className="shp-modal-title">{t('shared_save_copy_title')}</h3>
            <p className="shp-modal-desc">{t('shared_save_copy_desc')}</p>
            <input
              className="shp-modal-input"
              value={copyName}
              autoFocus
              placeholder={t('ws_modal_project_ph')}
              onChange={e => setCopyName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveCopy(); if (e.key === 'Escape' && !copyBusy) setCopyOpen(false); }}
            />
            <div className="shp-modal-actions">
              <button className="shp-modal-btn shp-modal-btn--ghost" onClick={() => setCopyOpen(false)} disabled={copyBusy}>{t('cancel')}</button>
              <button className="shp-modal-btn shp-modal-btn--primary" onClick={handleSaveCopy} disabled={copyBusy || !copyName.trim()}>
                {copyBusy ? <span className="shp-spinner shp-spinner--sm" /> : null}
                {copyBusy ? t('ws_creating') : t('shared_save_copy_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TopBar({ t, lang, setLang, projectName, view, setView, rooms, roomIdx, setRoomIdx, minimal, canEdit, loggedIn, onSaveCopy, hideCta, canCopy = true }) {
  return (
    <div className="shp-bar">
      <div className="shp-brand">
        <img src="/images/Tunify Original logo tab.png" alt="Tunify" className="shp-logo-img" />
        <span className="shp-name">Tunify</span>
      </div>

      {!minimal && (
        <>
          <div className="shp-project">
            {projectName && <span className="shp-project-name" title={projectName}>{projectName}</span>}
            <span className={`shp-pill${canEdit ? ' shp-pill--edit' : ''}`}>{canEdit ? t('shared_editable') : t('shared_readonly')}</span>
          </div>

          {/* Full house ↔ Rooms toggle */}
          <div className="shp-toggle" role="tablist">
            <button
              className={`shp-toggle-btn${view === 'house' ? ' active' : ''}`}
              onClick={() => setView('house')}
              role="tab" aria-selected={view === 'house'}
            >{t('shared_view_house')}</button>
            <button
              className={`shp-toggle-btn${view === 'room' ? ' active' : ''}`}
              onClick={() => setView('room')}
              role="tab" aria-selected={view === 'room'}
              disabled={rooms.length === 0}
            >{t('shared_view_rooms')}</button>
          </div>

          {/* Room picker — only when viewing rooms and there's more than one */}
          {view === 'room' && rooms.length > 1 && (
            <select
              className="shp-room-select"
              value={roomIdx}
              onChange={(e) => setRoomIdx(Number(e.target.value))}
              aria-label={t('shared_view_rooms')}
            >
              {rooms.map((r, i) => (
                <option key={r.id} value={i}>{r.label || `Room ${i + 1}`}</option>
              ))}
            </select>
          )}
        </>
      )}

      <div className="shp-bar-right">
        <div className="shp-langs">
          {LANGS.map((l) => (
            <button
              key={l.code}
              className={`shp-lang-btn${lang === l.code ? ' active' : ''}`}
              onClick={() => setLang(l.code)}
            >{l.label}</button>
          ))}
        </div>
        {/* A signed-in viewer normally gets "save a copy". On a PUBLISHED design
            they get the plain "try Tunify" link instead — the work on screen is
            someone's portfolio, not a starting point to duplicate. */}
        {hideCta ? null : loggedIn && !minimal && canCopy ? (
          <button type="button" className="shp-cta" onClick={onSaveCopy}>{t('shared_save_copy')}</button>
        ) : (
          <Link to="/" className="shp-cta">{t('shared_try')}</Link>
        )}
      </div>
    </div>
  );
}
